import React from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePendingMovies, useApproveMovie, useProcessingContent, useRetryPushTrailer } from '../../hooks/useMovie';
import { Button } from '../../components/ui/button';
import { Link } from '../../components/common/NextAdapters';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../api/errors';
import PendingMovieEdits from './PendingMovieEdits';

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function AdminMoviesPage() {
  usePageTitle('Manage Movies · Kast');
  const { data: movies = [], isLoading, error, refetch } = usePendingMovies();
  const approveMutation = useApproveMovie();
  const { data: processing } = useProcessingContent();
  const retryMutation = useRetryPushTrailer();
  const stuckMovies = processing?.movies || [];

  const handle = async (movieId, status) => {
    try {
      const { data } = await approveMutation.mutateAsync({ movieId, status });
      if (data?.liveNow === false) {
        toast.success("Approved — trailer is uploading to Bunny.net. It'll go live automatically once that finishes.");
      } else {
        toast.success(`Movie ${status.toLowerCase()}.`);
      }
      refetch();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Action failed'));
    }
  };

  const handleRetry = async (id) => {
    try {
      await retryMutation.mutateAsync(id);
      toast.success('Retry queued — running in the background.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Retry failed'));
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:film-slate-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Pending Movies</h1>
            <p className="text-xs text-muted-foreground">Review and approve movies awaiting publish</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {movies.length > 0 && (
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ring-1 ring-primary/25">
              {movies.length} pending
            </span>
          )}
          <Button size="sm" asChild className="gap-1.5">
            <Link href="/upload/movie">
              <Icon icon="ph:upload-simple-bold" />
              Upload Movie
            </Link>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : error ? (
        <EmptyState icon="ph:warning-circle-bold">{getApiErrorMessage(error, 'Failed to load pending movies.')}</EmptyState>
      ) : movies.length === 0 ? (
        <EmptyState icon="ph:check-circle-bold">No pending movies</EmptyState>
      ) : (
        <div className="flex flex-col gap-4">
          {movies.map((m) => (
            <div
              key={m.Id}
              className="flex flex-col gap-4 rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06] transition-colors hover:bg-white/[0.05] sm:flex-row"
            >
              {m.ThumbnailUrl && (
                <img
                  src={m.ThumbnailUrl}
                  alt={m.Title}
                  className="h-[170px] w-full shrink-0 rounded-lg object-cover sm:h-[135px] sm:w-[240px]"
                />
              )}
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground">{m.Title}</h3>
                  <p className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {m.CategoryName && <span>{m.CategoryName}</span>}
                    {m.UploadedBy && <span>by {m.UploadedBy}</span>}
                    <span>{m.DateCreated?.split('T')[0]}</span>
                  </p>
                  {m.Description && (
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground/80">
                      {m.Description?.slice(0, 160)}…
                    </p>
                  )}
                  <div className="mt-2.5 flex flex-wrap gap-4">
                    {m.TrailerUrl && (
                      <a
                        href={m.TrailerUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                      >
                        <Icon icon="ph:play-bold" className="size-3.5" />
                        Preview Trailer
                      </a>
                    )}
                    {m.VideoUrl && (
                      <>
                        <a
                          href={m.VideoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                        >
                          <Icon icon="ph:play-bold" className="size-3.5" />
                          Watch Full Movie
                        </a>
                        <a
                          href={m.VideoUrl}
                          download
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                        >
                          <Icon icon="ph:download-bold" className="size-3.5" />
                          Download
                        </a>
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    size="sm"
                    onClick={() => handle(m.Id, 'Approved')}
                    disabled={approveMutation.isPending}
                    className="gap-1.5 bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30 hover:bg-emerald-500/25"
                  >
                    <Icon icon="ph:check-bold" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handle(m.Id, 'Rejected')}
                    disabled={approveMutation.isPending}
                    className="gap-1.5 bg-destructive/10 text-destructive ring-1 ring-destructive/25 hover:bg-destructive/20"
                  >
                    <Icon icon="ph:x-bold" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PendingMovieEdits />

      {stuckMovies.length > 0 && (
        <>
          <h2 className="mb-2.5 mt-8 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
            <Icon icon="ph:clock-bold" className="size-3.5" />
            Waiting on Bunny.net ({stuckMovies.length})
          </h2>
          <div className="flex flex-col gap-3">
            {stuckMovies.map((m) => (
              <div key={m.Id} className="flex items-start gap-4 rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06]">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-foreground">{m.Title}</h3>
                  <p className="mt-1">
                    {m.BunnyPushError ? (
                      <span className="inline-flex items-center rounded-full bg-destructive/10 px-2.5 py-0.5 text-[11px] font-semibold text-destructive ring-1 ring-destructive/25">
                        Push failed ({m.BunnyPushAttempts} attempt{m.BunnyPushAttempts === 1 ? '' : 's'})
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-[#f0a818]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#f0a818] ring-1 ring-[#f0a818]/30">
                        Uploading…
                      </span>
                    )}
                  </p>
                  {m.BunnyPushError && <p className="mt-1.5 break-words text-xs text-destructive">{m.BunnyPushError}</p>}
                </div>
                <Button
                  size="sm"
                  onClick={() => handleRetry(m.Id)}
                  disabled={retryMutation.isPending}
                  className="shrink-0 gap-1.5 bg-[#f0a818]/10 text-[#f0a818] ring-1 ring-[#f0a818]/30 hover:bg-[#f0a818]/20"
                >
                  <Icon icon="ph:arrow-clockwise-bold" />
                  Retry
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
